import { useState, useEffect } from "react";

/* ═══════════════════════════════════════════════════════════
   YOURS TRULY — Landing Page
   
   Hero + Gallery Showcase + How It Works + Pricing + CTA
   All CTAs route to /create (AppV3 iceberg flow)
   
   Standalone — no external component dependencies.
   ═══════════════════════════════════════════════════════════ */

function useIsMobile(bp = 768) {
  const [m, setM] = useState(typeof window !== "undefined" && window.innerWidth <= bp);
  useEffect(() => {
    const h = () => setM(window.innerWidth <= bp);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, [bp]);
  return m;
}

/* ═══ DESIGN TOKENS ═══ */
const T = {
  bg: "#0a0a14",
  text: "#e8e6e2",
  textSoft: "#a0a0a8",
  textMuted: "#5a5a64",
  accent: "#BFC1C2",
  accentSub: "rgba(191,193,194,0.08)",
  border: "rgba(200,200,220,0.08)",
  borderLight: "rgba(200,200,220,0.06)",
  gold: "#c9a96e",
  goldSoft: "rgba(201,169,110,0.55)",
  goldFaint: "rgba(201,169,110,0.12)",
};

const SERIF = '"Cormorant Garamond", Georgia, serif';
const DISPLAY = '"Playfair Display", Georgia, serif';
const SANS = '"Outfit", Helvetica, sans-serif';

/* ═══ LOGO ═══ */
function YTLogo({ size = 36, color = "rgba(201,169,110,0.7)", dimColor = "rgba(201,169,110,0.15)", whorlColor = "rgba(201,169,110,0.12)" }) {
  return (
    <svg width={size} height={size} viewBox="-20 -20 40 40" fill="none" style={{ display: "block" }}>
      <path d="M-11 -14 C-9 -8 -8 -2 -7 2 C-7 8 -5 14 0 17" stroke={whorlColor} strokeWidth="0.4" fill="none" strokeLinecap="round" />
      <path d="M11 -14 C9 -8 8 -2 7 2 C7 8 5 14 0 17" stroke={whorlColor} strokeWidth="0.4" fill="none" strokeLinecap="round" />
      <path d="M-7 -9 C-5 -5 -2 -1.5 0 0.5" stroke={color} strokeWidth="0.9" fill="none" strokeLinecap="round" />
      <path d="M7 -9 C5 -5 2 -1.5 0 0.5" stroke={color} strokeWidth="0.9" fill="none" strokeLinecap="round" />
      <path d="M0 0.5 L0 11" stroke={color} strokeWidth="0.9" fill="none" strokeLinecap="round" />
      <path d="M-5.5 0.5 C-2 1.5 2 1.5 5.5 0.5" stroke={color} strokeWidth="0.9" fill="none" strokeLinecap="round" />
      <circle cx="-7" cy="-9" r="1.7" fill={color} />
      <circle cx="7" cy="-9" r="1.7" fill={color} />
      <circle cx="0" cy="0.5" r="2" fill={color} />
      <circle cx="0" cy="11" r="1.4" fill={color} />
      <circle cx="-5.5" cy="0.5" r="1.2" fill={color} />
      <circle cx="5.5" cy="0.5" r="1.2" fill={color} />
      <circle cx="-4" cy="-5" r="0.5" fill={dimColor} />
      <circle cx="4" cy="-5" r="0.5" fill={dimColor} />
      <circle cx="-3" cy="7" r="0.4" fill={dimColor} />
      <circle cx="3" cy="7" r="0.4" fill={dimColor} />
    </svg>
  );
}

/* ═══ GALLERY DATA ═══ */
const SHOWCASE = [
  {
    name: "The Bond Helix",
    tagline: "Two souls intertwined in crystal and gold",
    image: "/dna_cats_hero.png",
    color: "130,100,200",
    story: "A double helix of faceted crystal spirals upward — and cradled at its heart, two cats curl into each other, one dark as midnight, one warm as amber. The strands aren't just structure, they're the invisible thing that holds two lives together. The crystal butterfly wings at the center catch every angle of light differently, because that's what real connection does — it refracts. It multiplies. It makes everything around it more beautiful.",
    for: "Made for Deepali",
    detail: "Kathak dancer • Cancer researcher • Cat mom",
  },
  {
    name: "The Roar and the Resonance",
    tagline: "The stadium and the silence in one form",
    image: "/roar_resonance_hero.png",
    color: "74,142,194",
    story: "A man who contains both the stadium and the silence — the wave that breaks and the depth beneath it. Piano keys ripple along the curve like a soundtrack only he can hear, and at the crest, the ball hangs in that moment before everything erupts.",
    for: "Made for Pratik",
    detail: "Chelsea fan • Piano player • Hans Zimmer devotee",
  },
  {
    name: "The Metamorphosis Engine",
    tagline: "Where raw power learns to fly",
    image: "/butterfly_guitar_hero.png",
    color: "120,120,140",
    story: "He started in a garage with a busted amp and a dream that didn't fit the town he grew up in. The wheels kept turning, the music kept climbing, and somewhere between the grind and the grace, the thing he was building grew wings. This sculpture holds the exact moment the engine became the butterfly.",
    for: "Made for Marcus",
    detail: "Guitarist • Gearhead • Self-made dreamer",
  },
];

/* ═══ CHROME BUTTON STYLE ═══ */
const chromeBtn = {
  background: "linear-gradient(90deg, #b0b0c0 0%, #d8d8e8 15%, #ffffff 30%, #d8d8e8 45%, #a0a0b8 55%, #c8c8d8 70%, #e8e8f0 85%, #b0b0c0 100%)",
  backgroundSize: "200% 100%",
  border: "1px solid rgba(255,255,255,0.5)",
  color: "#2a2830",
  borderRadius: 28,
  padding: "16px 44px",
  fontSize: 14,
  fontWeight: 500,
  cursor: "pointer",
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  boxShadow: "0 4px 20px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.6)",
  textShadow: "0 1px 0 rgba(255,255,255,0.5)",
  transition: "all 0.3s ease",
  fontFamily: SANS,
};

const chromeBtnSm = {
  ...chromeBtn,
  padding: "10px 24px",
  fontSize: 13,
  borderRadius: 20,
  letterSpacing: "0.05em",
  boxShadow: "0 2px 12px rgba(0,0,0,0.15), inset 0 1px 0 rgba(255,255,255,0.6)",
};

/* ═══ MAIN ═══ */
export default function Landing({ onBegin }) {
  const isMobile = useIsMobile();
  const [visible, setVisible] = useState(false);

  useEffect(() => { setTimeout(() => setVisible(true), 100); }, []);

  const begin = () => (onBegin ? onBegin() : (window.location.href = "/create"));

  return (
    <div style={{ minHeight: "100vh", background: T.bg, color: T.text, fontFamily: SANS, overflow: "auto", position: "relative" }}>

      {/* ═══ GLOBAL STYLES ═══ */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;1,300;1,400&family=Playfair+Display:ital,wght@0,400;0,500;0,600;1,400;1,500&family=Outfit:wght@300;400;500;600;700&display=swap');
        html, body { background: ${T.bg}; margin: 0; padding: 0; }
        * { box-sizing: border-box; margin: 0; padding: 0; }
        button:focus { outline: none; }
        @keyframes heartbeat { 0%,100%{transform:scale(1)} 14%{transform:scale(1.08)} 28%{transform:scale(1)} 42%{transform:scale(1.05)} 56%{transform:scale(1)} }
        @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.3} }
        @keyframes chromeSweep { 0%{background-position:200% center} 100%{background-position:-200% center} }
        @keyframes expandFade { from{opacity:0;transform:translateY(30px)} to{opacity:1;transform:translateY(0)} }
        .chrome-btn { transition: all 0.3s ease !important; position: relative; overflow: hidden; }
        .chrome-btn:hover { animation: chromeSweep 1.5s ease forwards; box-shadow: 0 6px 30px rgba(0,0,0,0.25), inset 0 1px 0 rgba(255,255,255,0.7), 0 0 20px rgba(200,200,220,0.3) !important; transform: scale(1.03); }
        ::-webkit-scrollbar { width: 4px; }
        ::-webkit-scrollbar-thumb { background: rgba(181,173,165,0.3); border-radius: 4px; }
      `}</style>

      {/* ═══ CONSTELLATION STARS ═══ */}
      <div style={{ position: "fixed", inset: 0, pointerEvents: "none", zIndex: 0 }}>
        {Array.from({ length: 60 }).map((_, i) => (
          <div key={i} style={{
            position: "absolute",
            width: Math.random() > 0.8 ? 2 : 1,
            height: Math.random() > 0.8 ? 2 : 1,
            borderRadius: "50%",
            background: "rgba(200,200,220," + (0.1 + Math.random() * 0.35) + ")",
            left: Math.random() * 100 + "%",
            top: Math.random() * 100 + "%",
            animation: "pulse " + (2 + Math.random() * 4) + "s ease-in-out " + (Math.random() * 3) + "s infinite",
          }} />
        ))}
      </div>

      {/* ═══ NAV ═══ */}
      <nav style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: isMobile ? "14px 20px" : "20px 40px",
        position: "fixed", top: 0, left: 0, right: 0, zIndex: 50,
        background: T.bg + "ee", backdropFilter: "blur(16px)",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ animation: "heartbeat 2.4s ease-in-out infinite" }}>
            <YTLogo size={48} />
          </div>
          <span style={{ fontSize: isMobile ? 15 : 18, fontFamily: SERIF, fontStyle: "italic", color: T.text }}>
            Yours Truly
          </span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <a href="/gallery" style={{ fontSize: 13, color: "rgba(201,169,110,0.7)", textDecoration: "none", fontFamily: SERIF, fontStyle: "italic" }}>Gallery</a>
          <button className="chrome-btn" onClick={begin} style={isMobile ? { ...chromeBtnSm, padding: "8px 18px", fontSize: 12 } : chromeBtnSm}>
            Start Creating
          </button>
        </div>
      </nav>

      {/* ═══ HERO ═══ */}
      <section style={{
        paddingTop: isMobile ? 110 : 140, textAlign: "center",
        maxWidth: 800, margin: "0 auto",
        padding: isMobile ? "110px 20px 60px" : "140px 32px 80px",
        opacity: visible ? 1 : 0, transform: visible ? "translateY(0)" : "translateY(20px)",
        transition: "all 1s ease",
      }}>
        <div style={{ fontSize: isMobile ? 10 : 11, color: T.accent, letterSpacing: "0.25em", textTransform: "uppercase", fontWeight: 500, marginBottom: 16 }}>
          Sculpted by story
        </div>
        <h1 style={{
          fontSize: isMobile ? "30px" : "clamp(34px, 5.5vw, 58px)",
          fontFamily: DISPLAY, fontWeight: 400, lineHeight: 1.2,
          color: T.text, fontStyle: "italic", marginBottom: 20,
        }}>
          Their story, their world, their soul &mdash; captured in a sculpture made only for them.
        </h1>
        <p style={{
          fontSize: isMobile ? 15 : 17, color: T.textSoft, lineHeight: 1.7,
          maxWidth: 560, margin: "0 auto 32px", fontWeight: 300,
        }}>
          They say gifts come from the heart. Why don&rsquo;t we keep it that way? Starting at $49.
        </p>
        <button className="chrome-btn" onClick={begin} style={chromeBtn}>
          Turn a Memory Into Art
        </button>
      </section>

      {/* ═══ SHOWCASE — GALLERY CARDS ═══ */}
      <section style={{ padding: isMobile ? "20px 0 40px" : "40px 0 80px", position: "relative" }}>
        <div style={{ textAlign: "center", marginBottom: isMobile ? 24 : 40, padding: "0 20px" }}>
          <div style={{ fontSize: 9, letterSpacing: "0.25em", textTransform: "uppercase", color: T.accent, marginBottom: 8, fontWeight: 500 }}>
            Gallery
          </div>
          <h2 style={{ fontSize: isMobile ? 24 : 36, fontFamily: DISPLAY, fontWeight: 400, fontStyle: "italic", color: T.text }}>
            Every piece tells a story
          </h2>
        </div>

        <div style={{
          display: "flex", gap: isMobile ? 16 : 24,
          padding: isMobile ? "0 16px" : "0 40px",
          justifyContent: isMobile ? "flex-start" : "center",
          overflowX: isMobile ? "auto" : "visible",
          WebkitOverflowScrolling: "touch",
          scrollSnapType: isMobile ? "x mandatory" : "none",
          msOverflowStyle: "none", scrollbarWidth: "none",
        }}>
          {SHOWCASE.map((s, i) => {
            const cardC = "rgba(" + s.color + ",";
            return (
              <div key={i}
                onClick={() => { window.location.href = "/gallery?s=" + i; }}
                style={{
                  minWidth: isMobile ? 280 : 320, maxWidth: isMobile ? 280 : 320,
                  scrollSnapAlign: "center", cursor: "pointer", borderRadius: 20,
                  overflow: "hidden", border: "1px solid " + T.border, background: T.bg,
                  transition: "all 0.4s cubic-bezier(0.4,0,0.2,1)",
                  transform: visible ? "translateY(0)" : "translateY(30px)",
                  opacity: visible ? 1 : 0,
                  transitionDelay: (0.2 + i * 0.15) + "s",
                  position: "relative",
                }}
                onMouseEnter={e => e.currentTarget.style.borderColor = cardC + "0.4)"}
                onMouseLeave={e => e.currentTarget.style.borderColor = T.border}
              >
                {/* Image */}
                <div style={{ position: "relative", aspectRatio: "3/4", overflow: "hidden" }}>
                  <img src={s.image} alt={s.name} style={{
                    width: "100%", height: "100%", objectFit: "cover", display: "block",
                    transition: "transform 0.6s ease",
                  }}
                    onMouseEnter={e => e.target.style.transform = "scale(1.05)"}
                    onMouseLeave={e => e.target.style.transform = "scale(1)"}
                  />
                  <div style={{ position: "absolute", inset: 0, background: "linear-gradient(180deg, transparent 50%, rgba(6,6,10,0.85) 100%)" }} />
                  <div style={{ position: "absolute", bottom: 16, left: 16, right: 16 }}>
                    <div style={{
                      fontSize: 22, fontFamily: DISPLAY, fontWeight: 400, fontStyle: "italic",
                      color: "#fff", lineHeight: 1.15, textShadow: "0 2px 12px rgba(0,0,0,0.5)",
                    }}>{s.name}</div>
                    <div style={{ fontSize: 11, color: "rgba(255,255,255,0.5)", fontFamily: DISPLAY, fontStyle: "italic", marginTop: 3 }}>
                      {s.tagline}
                    </div>
                  </div>
                </div>
                {/* Card bottom */}
                <div style={{ padding: "14px 16px" }}>
                  <div style={{ fontSize: 9, letterSpacing: "0.15em", textTransform: "uppercase", color: T.accent, fontWeight: 500, marginBottom: 4 }}>
                    {s.for}
                  </div>
                  <div style={{ fontSize: 11, color: T.textSoft, lineHeight: 1.5 }}>{s.detail}</div>
                  <div style={{ display: "flex", alignItems: "center", gap: 4, marginTop: 10, fontSize: 10, color: cardC + "0.7)", fontWeight: 500 }}>
                    <span>Decode this sculpture</span>
                    <span style={{ fontSize: 13 }}>&#8599;</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Gallery CTA */}
        <div style={{ textAlign: "center", marginTop: 32, paddingBottom: 8 }}>
          <a href="/gallery" style={{
            display: "inline-flex", alignItems: "center", gap: 8,
            fontSize: 14, fontFamily: SERIF, fontStyle: "italic",
            color: "rgba(201,169,110,0.8)", textDecoration: "none",
            padding: "12px 28px", borderRadius: 24,
            border: "1px solid rgba(201,169,110,0.2)",
            transition: "all 0.3s ease",
          }}>
            View more in Gallery <span style={{ fontSize: 18 }}>{"\u2192"}</span>
          </a>
        </div>
      </section>

      {/* ═══ HOW IT WORKS ═══ */}
      <section style={{ padding: "60px 32px", maxWidth: 900, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 36 }}>
          <h2 style={{ fontSize: isMobile ? 22 : 28, fontFamily: SERIF, fontWeight: 400, fontStyle: "italic", color: T.text }}>
            How it works
          </h2>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)", gap: 20 }}>
          {[
            { i: "1", t: "Share Your Memory", d: "Have a conversation with our AI about the person, the bond, and the moment that changed everything." },
            { i: "2", t: "We Fuse Your Story", d: "AI takes the objects, emotions, and elements from your story and fuses them into a single surreal sculpture — something that has never existed before." },
            { i: "3", t: "Own It Forever", d: "Receive your one-of-a-kind digital sculpture with a full decode of every element — how each piece of their world became anatomy of the form." },
          ].map((x, j) => (
            <div key={j} style={{ padding: "28px 24px", borderRadius: 16, border: "1px solid " + T.border, textAlign: "center" }}>
              <div style={{
                width: 40, height: 40, borderRadius: "50%", border: "1.5px solid " + T.accent,
                display: "flex", alignItems: "center", justifyContent: "center",
                margin: "0 auto 14px", fontSize: 16, fontFamily: SERIF, color: T.accent, fontWeight: 500,
              }}>{x.i}</div>
              <div style={{ fontSize: 15, fontWeight: 500, color: T.text, marginBottom: 6 }}>{x.t}</div>
              <div style={{ fontSize: 13, color: T.textSoft, lineHeight: 1.7 }}>{x.d}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ═══ PRICING ═══ */}
      <section style={{ padding: "40px 32px", maxWidth: 800, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 24 }}>
          <h2 style={{ fontSize: isMobile ? 22 : 28, fontFamily: SERIF, fontWeight: 400, fontStyle: "italic", color: T.text }}>
            Starting at $49
          </h2>
          <p style={{ fontSize: 13, color: T.textSoft, marginTop: 8 }}>
            The AI conversation and concept art are completely free. You only pay when you love the design.
          </p>
        </div>
        <div style={{
          maxWidth: 380, margin: "0 auto", padding: "32px 28px", borderRadius: 20,
          border: "1.5px solid " + T.accent, background: T.accentSub, textAlign: "center",
        }}>
          <div style={{ fontSize: 42, fontFamily: SERIF, color: T.accent, fontWeight: 500 }}>$49</div>
          <div style={{ fontSize: 18, fontWeight: 500, color: T.text, marginTop: 4, marginBottom: 12 }}>
            The Sculpture
          </div>
          <div style={{ fontSize: 13, color: T.textSoft, lineHeight: 1.8 }}>
            AI-designed fusion sculpture<br />
            High-res digital artwork<br />
            Full decode of every element<br />
            Yours in minutes
          </div>
          <button className="chrome-btn" onClick={begin} style={{
            ...chromeBtn, marginTop: 20, padding: "14px 36px",
          }}>Start Your Story</button>
        </div>
      </section>

      {/* ═══ OCCASIONS ═══ */}
      <section style={{ padding: "40px 32px", maxWidth: 700, margin: "0 auto", textAlign: "center" }}>
        <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: 10 }}>
          {"Mother's Day,Birthdays,Anniversaries,Weddings,Memorials,Graduations,Friendships,Pet Tributes".split(",").map(o => (
            <span key={o} style={{
              padding: "7px 16px", borderRadius: 20, border: "1px solid " + T.border,
              fontSize: 12, color: T.textSoft,
            }}>{o}</span>
          ))}
        </div>
      </section>

      {/* ═══ FINAL CTA ═══ */}
      <section style={{ padding: "60px 32px", textAlign: "center" }}>
        <h2 style={{ fontSize: isMobile ? 22 : 28, fontFamily: SERIF, fontWeight: 400, fontStyle: "italic", color: T.text, marginBottom: 8 }}>
          What memory would you hold forever?
        </h2>
        <p style={{ fontSize: 13, color: T.textSoft, marginBottom: 20, maxWidth: 400, margin: "0 auto 20px" }}>
          The conversation is free. The concept art is free. You only pay when you love the design.
        </p>
        <button className="chrome-btn" onClick={begin} style={chromeBtn}>
          Begin Your Story &rarr;
        </button>
      </section>

      {/* ═══ FOOTER ═══ */}
      <footer style={{
        padding: "32px 40px", borderTop: "1px solid " + T.border,
        display: "flex", justifyContent: "space-between",
        fontSize: 11, color: T.textMuted,
      }}>
        <span style={{ fontFamily: SERIF, fontStyle: "italic", fontSize: 13 }}>Yours Truly</span>
        <span>&copy; 2026</span>
      </footer>
    </div>
  );
}
